import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { useI18n } from "../../i18n";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { big6 } from "../../data/convictConditioning";

interface ChainMeta {
  id: string;
  name: { de: string; hu: string; en: string };
}

const CHAINS: ChainMeta[] = [
  { id: "pushup", name: { de: "Liegestütze", hu: "Fekvőtámasz", en: "Push-ups" } },
  { id: "pullup", name: { de: "Klimmzüge", hu: "Húzódzkodás", en: "Pull-ups" } },
  { id: "squat", name: { de: "Kniebeugen", hu: "Guggolás", en: "Squats" } },
  { id: "leg_raise", name: { de: "Beinheben", hu: "Lábemelés", en: "Leg Raises" } },
  { id: "bridge", name: { de: "Brücken", hu: "Híd", en: "Bridges" } },
  { id: "handstand_pushup", name: { de: "Handstand-Liegestütze", hu: "Kézállás nyomás", en: "Handstand Push-ups" } },
];

function BigSixTile({
  chain,
  index,
  locale,
  reduceMotion,
}: {
  chain: ChainMeta;
  index: number;
  locale: "de" | "hu" | "en";
  reduceMotion: boolean;
}): JSX.Element {
  const steps = useMemo(
    () => big6.filter((e) => e.category === chain.id),
    [chain.id]
  );
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (reduceMotion || steps.length <= 1) return;

    let intervalId: ReturnType<typeof setInterval>;
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        setStepIdx((prev) => (prev + 1) % steps.length);
      }, 3600);
    }, index * 600); // Stagger initial start per card (T-081 F-6)

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [reduceMotion, steps.length, index]);

  const current = steps[stepIdx] || steps[0];
  const stepNumber = current?.stepNumber ?? (stepIdx + 1);
  const percent = Math.round((stepNumber / 10) * 100);

  return (
    <li
      data-testid="big6-tile"
      data-chain={chain.id}
      className="group relative flex flex-col justify-between border border-[var(--color-line)] bg-[var(--color-slab)] p-5 transition-colors duration-[120ms] ease-[cubic-bezier(0.2,0,0,1)] hover:border-[var(--color-line-strong)] hover:bg-[var(--color-slab-2)]"
      style={{ borderRadius: "var(--radius-card)", minHeight: "200px" }}
    >
      <div>
        <div className="flex items-center justify-between">
          <p className="font-subhead text-xs uppercase tracking-[0.08em] text-[var(--color-lime)]">
            {chain.name[locale] || chain.name.en}
          </p>
          <span className="font-numeric text-xs uppercase tracking-wider text-[var(--color-ink-mute)]">
            {stepNumber} / 10
          </span>
        </div>
        <h3 className="font-display mt-2 text-2xl uppercase leading-[1.15] tracking-[0.02em] text-[var(--color-ink-hi)] transition-opacity duration-300">
          {current?.name[locale] || current?.name.en}
        </h3>
        {current?.standard && (
          <p className="mt-1 font-sans text-xs text-[var(--color-ink-mute)]">
            {locale === "de" ? "Ziel" : locale === "hu" ? "Cél" : "Target"}: {current.standard.masteryReps} reps
          </p>
        )}
      </div>

      <div className="mt-5 pt-3 border-t border-[var(--color-line)]">
        <div
          className="h-[3px] w-full bg-[var(--color-line)]"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${chain.name[locale]}: ${percent}%`}
        >
          <div
            className="h-full bg-[var(--color-lime)] transition-all duration-500 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </li>
  );
}

export function BigSixGrid(): JSX.Element {
  const { t, locale } = useI18n();
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  return (
    <section
      id="big6"
      aria-label="big-six"
      className="mx-auto w-full max-w-[1280px] px-6 py-20 sm:px-10"
    >
      <header className="mb-10 max-w-prose">
        <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]">
          {t("brand.name")}
        </p>
        <h2 className="font-display mt-2 text-4xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)] sm:text-5xl">
          {t("landing.big6.title")}
        </h2>
        <p
          className="mt-3 font-sans text-base text-[var(--color-ink-mute)]"
          style={{ lineHeight: 1.5 }}
        >
          {t("landing.big6.subtitle")}
        </p>
      </header>

      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CHAINS.map((chain, index) => (
          <BigSixTile
            key={chain.id}
            chain={chain}
            index={index}
            locale={locale}
            reduceMotion={reduceMotion}
          />
        ))}
      </ul>
    </section>
  );
}

